import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/monitoring';


/**
 * Single source of truth for calling admin edge functions.
 *
 * Why this exists: `supabase.functions.invoke()` attaches whatever token the
 * client currently holds. If the stored session has expired (or was restored
 * from storage without a refresh) it silently falls back to the anon key, and
 * every admin-only function answers 401 `unauthorized`. supabase-js then throws
 * a FunctionsHttpError whose `.message` is the useless string
 * "Edge Function returned a non-2xx status code", and the real JSON body
 * (`{ error: 'unauthorized', reason: 'anon_key_only' }`) is thrown away.
 *
 * This helper:
 *   1. forces a session refresh when the access token is missing/near expiry,
 *   2. sends the access token explicitly in the Authorization header,
 *   3. reads the real JSON error body back out of the failed Response,
 *   4. returns a normalised { ok, data, status, error, reason, authFailed }.
 */

export interface EdgeResult<T = any> {
  ok: boolean;
  data: T | null;
  status: number | null;
  /** Machine readable code from the function body, e.g. 'unauthorized'. */
  error: string | null;
  /** Extra detail from the function body, e.g. 'admin_only'. */
  reason: string | null;
  /** True when the failure was authentication/authorisation, not business logic. */
  authFailed: boolean;
  /** Human-readable sentence safe to show an operator. */
  message: string | null;
}

const AUTH_CODES = ['unauthorized', 'forbidden'];

/** Grab a usable access token, refreshing first if it is absent or stale. */
export const getAdminAccessToken = async (): Promise<string | null> => {
  const { data } = await supabase.auth.getSession();
  let session = data.session;

  const expiresAt = session?.expires_at ? session.expires_at * 1000 : 0;
  const staleOrMissing = !session?.access_token || (expiresAt > 0 && expiresAt - Date.now() < 60_000);

  if (staleOrMissing) {
    const { data: refreshed } = await supabase.auth.refreshSession();
    if (refreshed?.session?.access_token) session = refreshed.session;
  }

  return session?.access_token || null;
};

export const describeEdgeError = (r: Pick<EdgeResult, 'error' | 'reason' | 'status'>, es = false): string => {
  const code = r.error || '';
  const reason = r.reason || '';

  if (code === 'no_session')
    return es
      ? 'Su sesión de administrador expiró. Vuelva a iniciar sesión y repita la acción.'
      : 'Your admin session expired. Sign in again and repeat the action.';
  if (code === 'unauthorized')
    return es
      ? `Sesión no válida (${reason || 'token'}). Cierre sesión, vuelva a entrar y repita la acción.`
      : `Session not accepted (${reason || 'token'}). Log out, log back in, then repeat the action.`;
  if (code === 'forbidden')
    return es
      ? 'Su cuenta no tiene el permiso de administrador (mkt_profiles.is_admin).'
      : 'Your account does not carry the administrator flag (mkt_profiles.is_admin).';
  if (code === 'already_exists')
    return es ? 'Ya existe una cuenta con ese correo.' : 'An account already uses that email.';
  if (code)
    return es ? `Error: ${code}${reason ? ` (${reason})` : ''}` : `Error: ${code}${reason ? ` (${reason})` : ''}`;
  return es ? `La función respondió ${r.status ?? '?'}.` : `The function responded ${r.status ?? '?'}.`;
};

export async function invokeEdge<T = any>(
  name: string,
  body: Record<string, unknown> = {},
  opts: { requireAuth?: boolean } = {}
): Promise<EdgeResult<T>> {
  const requireAuth = opts.requireAuth !== false;
  const token = await getAdminAccessToken();

  if (requireAuth && !token) {
    const base = {
      ok: false,
      data: null,
      status: 401,
      error: 'no_session',
      reason: 'client_has_no_access_token',
      authFailed: true,
    };
    return { ...base, message: describeEdgeError(base) };
  }

  const { data, error } = await supabase.functions.invoke(name, {
    body,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

  if (!error) {
    const code = (data as any)?.error ? String((data as any).error) : null;
    const reason = (data as any)?.reason ? String((data as any).reason) : null;
    const result = {
      ok: !code,
      data: (data as T) ?? null,
      status: 200,
      error: code,
      reason,
      authFailed: Boolean(code && AUTH_CODES.includes(code)),
    };
    return { ...result, message: code ? describeEdgeError(result) : null };
  }

  // Non-2xx: dig the real JSON body out of the thrown FunctionsHttpError.
  let status: number | null = null;
  let payload: any = null;
  const ctx: any = (error as any)?.context;
  if (ctx && typeof ctx.text === 'function') {
    status = typeof ctx.status === 'number' ? ctx.status : null;
    try {
      const text = await ctx.text();
      payload = text ? JSON.parse(text) : null;
    } catch {
      payload = null;
    }
  } else if (typeof ctx?.status === 'number') {
    status = ctx.status;
  }

  const code = payload?.error ? String(payload.error) : null;
  const reason = payload?.reason ? String(payload.reason) : payload?.detail ? String(payload.detail) : null;
  const result = {
    ok: false,
    data: (payload as T) ?? null,
    status,
    error: code,
    reason,
    authFailed: status === 401 || status === 403 || Boolean(code && AUTH_CODES.includes(code)),
  };

  // Backend failure reaching production: report it (auth failures are tagged
  // 'auth', everything else 'edge'). Reporting is fire-and-forget and can
  // never change the result the caller receives. The observability endpoint
  // itself is excluded so a reporting outage cannot recurse.
  if (name !== 'observability') {
    captureError({
      message: `Edge function "${name}" failed: ${code || `HTTP ${status ?? '?'}`}`,
      kind: result.authFailed ? 'auth' : 'edge',
      level: result.authFailed ? 'warning' : 'error',
      area: name,
      context: { function: name, status, code, reason },
    });
  }

  return { ...result, message: describeEdgeError(result) };
}
